import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { CreateTurbineGroupGQL, DeleteTurbineGroupGQL, GetPlantsAndTurbinesGQL, GetPlantsAndTurbinesQuery, UpdateTurbineGroupGQL, GetUserTurbineGroupsGQL, GetUserTurbineGroupsQuery } from "app/shared/services-api-v2/graphql";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";


export interface TurbineGroup {
  _id_park: string;
  turbine_groups: {
    id: string;
    name: string;
    turbines: string[];
  }[];
}

@Injectable({ providedIn: "root" }) 
export class DeviceGroupsService {

  private groupsData: TurbineGroup[];
  private groupsData$: BehaviorSubject<TurbineGroup[]> = new BehaviorSubject<TurbineGroup[]>([]);

  constructor(
    private apollo: Apollo,
    private getUserTurbineGroupsGQL: GetUserTurbineGroupsGQL,
    private getPlantsAndTurbinesGQL: GetPlantsAndTurbinesGQL,
    private createTurbineGroupGQL: CreateTurbineGroupGQL,
    private updateTurbineGroupGQL: UpdateTurbineGroupGQL,
    private deleteTurbineGroupGQL: DeleteTurbineGroupGQL,
  ) {

  }

  public initDeviceGroupsData() {
    return this.getGroupsData().pipe(take(1)).toPromise()
  }

  public getDeviceGroupsData$(): Observable<TurbineGroup[]> {
    return this.groupsData$.asObservable();
  }

  public getPlantsAndTurbinesData() {
    return this.apollo.use('v2_http').query({
      query: this.getPlantsAndTurbinesGQL.document,
    }).pipe(map((result: Apollo.QueryResult<GetPlantsAndTurbinesQuery>) => result.data.PARKS.map(park => ({
      id: park._id,
      displayName: park.displayName,
      turbines: park.TURBINES.map(turbine => ({
        id: turbine._id,
        displayName: turbine.displayName.replace(/_/g, ''),
        _id_park: park._id,
      })).sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { numeric: true }))
    })).sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { numeric: true }))
    ));
  }
  public getGroupsData(): Observable<TurbineGroup[]> {
    return this.apollo.use('v2_http').query({
      query: this.getUserTurbineGroupsGQL.document,
      fetchPolicy: 'no-cache',
    }).pipe(map((result: Apollo.QueryResult<GetUserTurbineGroupsQuery>) => {
      const turbinesGroups = result.data.PORTFOLIO.USER_TURBINE_GROUPS.map(
        group => ({
          _id_park: group._id_park,
          turbine_groups: group.turbine_groups.map(turbineGroup => ({
            id: turbineGroup._id,
            name: turbineGroup.name,
            turbines: turbineGroup.turbines.map(turbine => turbine.id),
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        })
      );
      this.groupsData$.next(turbinesGroups);
      this.groupsData = turbinesGroups;
      return turbinesGroups;
    }));
  }

  public createDeviceGroup(name: string, parkId: string, turbineIds: string[]): Observable<boolean> {
    return this.apollo.use('v2_http').mutate({
      mutation: this.createTurbineGroupGQL.document,
      variables: {
        name: name,
        _id_park: parkId,
        turbines: turbineIds,
      }
    }).pipe(catchError((error) => of(null)), map((result: any) => !!result?.data?.createCustomTurbineGroup?.success));
  }

  public updateDeviceGroup(groupId: string, name: string, turbineIds: string[], parkId: string): Observable<boolean> {

    return this.apollo.use('v2_http').mutate({
      mutation: this.updateTurbineGroupGQL.document,
      variables: {
        _id_group: groupId,
        _id_park: parkId,
        name: name,
        turbines: turbineIds,
      }
    }).pipe(catchError((error) => of(null)), map((result: any) => !!result?.data?.updateCustomTurbineGroup?.success));
  }

  public deleteDeviceGroup(groupId: string, parkId: string): Observable<boolean> {
    return this.apollo.use('v2_http').mutate({
      mutation: this.deleteTurbineGroupGQL.document,
      variables: {
        _id_group: groupId,
        _id_park: parkId,
      }
    }).pipe(catchError((error) => of(null)), map((result: any) => !!result?.data?.deleteCustomTurbineGroup?.success));

  }

  public getGroupsDataSync(): TurbineGroup[] {
    return this.groupsData;
  }
}